'use client';

/**
 * ObeyaPlanCommercialNarrativeCard — the LLM-written negotiation read
 * over usePlanCommercialView's facts (see plan_story.py's
 * build_plan_commercial_prompt). Deliberately NOT SynthesisCard: that
 * shell is coupled to {urgency, recommendations, priority pills} — a
 * health-triage framing this narrative explicitly avoids (positions,
 * never verdicts; no directive recommendations, no product swaps).
 * A plain paragraph is the honest shape for what this actually is.
 *
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanCommercialView } from './use-plan-commercial-view';

export function ObeyaPlanCommercialNarrativeCard() {
  const { contentKey } = useCardIdentity();
  const { data, queryKey, mcpId, strategy } = usePlanCommercialView();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_commercial_narrative',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: data?.narrative || 'Plan commercial position narrative',
    json: data ? { narrative: data.narrative, basis: data.basis } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, queryKey]));

  if (!mcpId) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm h-full flex flex-col">
      <div className={cn(text.heading)}>Negotiation read</div>
      {!data ? (
        <div className={cn(text.muted, 'mt-3')}>Generating…</div>
      ) : data.narrative ? (
        <p className={cn(text.body, 'leading-relaxed mt-3')}>{data.narrative}</p>
      ) : (
        <div className={cn(text.muted, 'mt-3')}>Nothing stands out yet.</div>
      )}
    </div>
  );
}
